import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import {
  Shield, UserCog, GraduationCap, ClipboardCheck, Save, RotateCcw,
  LayoutDashboard, Users, ShoppingBag, Lightbulb, BarChart3,
  MessageSquare, AlertTriangle, Wrench, FileText, CreditCard, Check
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_META = {
  admin: { label: "Administrator", icon: UserCog, color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30" },
  dozent: { label: "Dozent", icon: GraduationCap, color: "bg-green-500/10 text-green-500 border-green-500/30" },
  auditor: { label: "ISO-Auditor", icon: ClipboardCheck, color: "bg-purple-500/10 text-purple-500 border-purple-500/30" },
  super_admin: { label: "Super-Admin", icon: Shield, color: "bg-red-500/10 text-red-500 border-red-500/30" },
};

const AREA_ICONS = {
  dashboard: LayoutDashboard, participants: Users, users: UserCog, courses: GraduationCap,
  shop: ShoppingBag, kvp: Lightbulb, statistics: BarChart3, messages: MessageSquare,
  warnings: AlertTriangle, settings: Wrench, content: FileText, qm: Shield, bookings: CreditCard,
};

const RightsManagement = () => {
  const [staff, setStaff] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editAreas, setEditAreas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [staffRes, areasRes] = await Promise.all([
        axios.get(`${API}/admin/all-permissions`),
        axios.get(`${API}/admin/areas`),
      ]);
      setStaff(staffRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditAreas([...(user.permissions?.allowed_areas || [])]);
  };

  const toggleArea = (areaId) => {
    setEditAreas((prev) =>
      prev.includes(areaId) ? prev.filter((a) => a !== areaId) : [...prev, areaId]
    );
  };

  const selectAll = () => setEditAreas(areas.map((a) => a.id));
  const deselectAll = () => setEditAreas([]);

  const handleSave = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/permissions/${editUser.id}`, { allowed_areas: editAreas });
      toast.success(`Berechtigungen für ${editUser.name} gespeichert`);
      setEditUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const roleMeta = (role) => ROLE_META[role] || ROLE_META.admin;

  return (
    <AdminLayout title="Rechteverwaltung">
      <div className="space-y-6" data-testid="rights-management">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Berechtigungen verwalten
            </CardTitle>
            <CardDescription>
              Legen Sie fest, auf welche Bereiche Ihre Mitarbeiter Zugriff haben.
              Super-Admins haben immer Zugriff auf alle Bereiche.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : staff.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">Keine Mitarbeiter vorhanden</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Bereiche</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.map((s) => {
                    const meta = roleMeta(s.role);
                    const Icon = meta.icon;
                    const allowedCount = s.permissions?.allowed_areas?.length || 0;
                    const isCustom = s.permissions?.is_custom;
                    return (
                      <TableRow key={s.id} data-testid={`staff-row-${s.email}`}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell className="text-muted-foreground">{s.email}</TableCell>
                        <TableCell>
                          <Badge className={`${meta.color} border`}>
                            <Icon className="h-3 w-3 mr-1" />
                            {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">{allowedCount} / {areas.length}</span>
                        </TableCell>
                        <TableCell>
                          {isCustom ? (
                            <Badge className="bg-blue-500/10 text-blue-500 border border-blue-500/30">Angepasst</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">Standard</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {s.role !== "super_admin" ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(s)}
                              data-testid={`edit-perms-${s.email}`}
                            >
                              <UserCog className="h-4 w-4 mr-1" />
                              Bearbeiten
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">Alle Rechte</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Permissions Dialog */}
        <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Berechtigungen bearbeiten
              </DialogTitle>
              <DialogDescription>
                {editUser?.name} ({roleMeta(editUser?.role).label})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-perms">
                  <Check className="h-3 w-3 mr-1" />
                  Alle
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-perms">
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Keine
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto pr-2">
                {areas.map((area) => {
                  const AreaIcon = AREA_ICONS[area.id] || FileText;
                  const checked = editAreas.includes(area.id);
                  return (
                    <div
                      key={area.id}
                      className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors cursor-pointer ${
                        checked ? "bg-primary/5 border-primary/30" : "bg-card border-border hover:bg-secondary/30"
                      }`}
                      onClick={() => toggleArea(area.id)}
                      data-testid={`perm-toggle-${area.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <AreaIcon className={`h-4 w-4 ${checked ? "text-primary" : "text-muted-foreground"}`} />
                        <Label className="cursor-pointer font-medium">{area.label}</Label>
                      </div>
                      <Switch checked={checked} onCheckedChange={() => toggleArea(area.id)} />
                    </div>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditUser(null)}>Abbrechen</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-permissions-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Speichern..." : "Speichern"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default RightsManagement;
